<template>
  <!-- The log in form (LogIn board): username or email and password in one
       group, Forgot password?, Log in. Used by /login and the wide Welcome. -->
  <form class="auth-form" novalidate @submit.prevent="submitForm">
    <auth-group :message="loginError">
      <auth-row
        v-model="username"
        name="username"
        placeholder="Username or email"
        autocomplete="username"
        :error="errorField === 'username'"
      />
      <auth-row
        v-model="password"
        type="password"
        name="password"
        placeholder="Password"
        autocomplete="current-password"
        :error="errorField === 'password'"
      />
    </auth-group>
    <div class="auth-aside">
      <router-link to="/forgot-password">Forgot password?</router-link>
    </div>
    <v-btn
      type="submit"
      class="cl-btn cl-btn--hero auth-cta"
      variant="outlined"
      :loading="loading"
    >
      Log in
    </v-btn>
  </form>
</template>

<script>
import cloudloop from "@/mixins/cloudloop";
import AuthGroup from "@/components/auth/AuthGroup.vue";
import AuthRow from "@/components/auth/AuthRow.vue";
import {
  flexibleLogIn,
  loginErrorText,
  publicChannelIsEmpty,
  validateLogin,
} from "@/kit/auth.js";
import { landingAfterAuth, safeRedirect } from "@/router/redirects.js";

export default {
  name: "LoginForm",
  components: { AuthGroup, AuthRow },
  mixins: [cloudloop],
  data() {
    return {
      username: "",
      password: "",
      loginError: "",
      errorField: null,
      loading: false,
    };
  },
  watch: {
    username() {
      this.clearError();
    },
    password() {
      this.clearError();
    },
  },
  methods: {
    clearError() {
      if (this.loading) return;
      this.loginError = "";
      this.errorField = null;
    },

    publicChannelIsEmpty() {
      return publicChannelIsEmpty(() => this.fetchPublicSessionHeaders("?view=list&limit=1"));
    },

    // A username goes through the store as before. An email logs in the
    // app's way (flexibleLogIn), then loads the account the token names,
    // as the store's logInUser does with a typed username.
    logIn(identifier, password) {
      if (!identifier.includes("@")) {
        return this.$store
          .dispatch("logInUser", { username: identifier, password })
          .then(() => identifier);
      }
      return flexibleLogIn(identifier, password).then(({ token, username }) => {
        window.localStorage.setItem("JWT", token);
        return this.$store.dispatch("fetchUser", username).then(() => username);
      });
    },

    submitForm() {
      if (this.loading) return;
      const invalid = validateLogin({
        username: this.username,
        password: this.password,
      });
      if (invalid) {
        this.loginError = invalid.message;
        this.errorField = invalid.field;
        return;
      }
      this.loading = true;
      this.loginError = "";
      this.errorField = null;
      this.logIn(this.username.trim(), this.password)
        .then(async () => {
          // SI-14: a pending invite, then a deep link (the router guard or a
          // checkout that required an account), then Get started when there
          // is nothing to browse yet, else Sessions: Mine.
          const pendingInvite =
            window.localStorage.getItem("pendingInviteToken");
          if (pendingInvite) window.localStorage.removeItem("pendingInviteToken");
          const redirect = this.$route.query.redirect;
          const here = this.$route.fullPath;
          const publicEmpty =
            !pendingInvite && !safeRedirect(redirect) ? await this.publicChannelIsEmpty() : false;
          // Moved on while that was asked (a sidebar place): stay there.
          if (this.$route.fullPath !== here) {
            this.loading = false;
            return;
          }
          this.$router.push(
            landingAfterAuth({ pendingInvite, redirect, publicEmpty, form: "login" }),
          );
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          this.loginError = loginErrorText(error);
          this.errorField = "password";
          console.error("error logging in", error);
        });
    },
  },
};
</script>
